import orderBy from 'lodash/orderBy';
import React from 'react';
import { SortDirection } from 'libs/end-to-end/src/ui-types';

export interface SortedTableColumn {
  id: string;
  direction: SortDirection;
}

interface UseTableSortingParams<T extends object> {
  initialSortedTableColumns: SortedTableColumn[];
  items: T[];
}

interface UseTableSortingPayload<T extends object> {
  sortedItems: T[];
  onSortChange: (params: SortedTableColumn[]) => void;
  /**
   * Returns the sorting info for a given column.
   */
  getSortingInfo: (params: { id: string }) => {
    direction: SortDirection;
    isActive: boolean;
  };
}
export const defaultSortDirection: SortDirection = 'desc';

export const useTableSorting = <T extends object>({
  initialSortedTableColumns,
  items,
}: UseTableSortingParams<T>): UseTableSortingPayload<T> => {
  const [sortedTableColumns, setSortedTableColumns] = React.useState<
    SortedTableColumn[]
  >(initialSortedTableColumns);

  const sortedItems = React.useMemo(() => {
    /**
     * We're using lodash's orderBy fn to do the sorting since it can easily support sorting by multiple columns.
     * This reduce function is just to transform the sortedTableColumns into the format orderBy expects.
     */
    const { columns, directions } = sortedTableColumns.reduce(
      (acc, { id, direction }) => {
        acc.columns.push(id);
        acc.directions.push(direction);

        return acc;
      },
      { columns: [], directions: [] } as {
        columns: string[];
        directions: SortDirection[];
      },
    );

    return orderBy(items, columns, directions);
  }, [items, sortedTableColumns]);

  const onSortChange = React.useCallback((params: SortedTableColumn[]) => {
    setSortedTableColumns(params);
  }, []);

  const getSortingInfo = React.useCallback(
    ({ id }: { id: string }) => {
      const sortedTableColumn = sortedTableColumns.find(
        (column) => column.id === id,
      );

      if (sortedTableColumn) {
        return {
          direction: sortedTableColumn.direction,
          isActive: true,
        };
      }

      return {
        direction: defaultSortDirection,
        isActive: false,
      };
    },
    [sortedTableColumns],
  );

  return {
    sortedItems,
    onSortChange,
    getSortingInfo,
  };
};
